import React from 'react';
import { Link } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { Eye } from 'react-bootstrap-icons';
import SortableHeaderCell from '../Generic/SortableHeaderCell';
import DeleteButton from '../Generic/DeleteButton';
import { deleteWorkerTimesheet } from '../../clients/worker_timesheet_client';
import { formatDateStr } from '../../config_and_helpers/helpers';

/**
 * Formats API ISO datetime as dd/mm/yyyy (date part only).
 *
 * @param {string|null|undefined} iso
 * @returns {string}
 */
function formatApiDate(iso) {
  if (!iso) return '';
  const datePart = String(iso).slice(0, 10);
  return formatDateStr(datePart);
}

/**
 * List API `total_hours_worked` (JSON number, two decimal places on server). Show em dash when missing.
 *
 * @param {number|null|undefined} value
 * @returns {string}
 */
function formatTotalHours(value) {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

/**
 * Uses backend rollup count when present; otherwise derives count from `daily_hours`.
 *
 * @param {Object} row
 * @param {'is_sick_day'|'is_off_day'} key
 * @param {'total_sick_days'|'total_off_days'} rollupField
 * @returns {number}
 */
function getDayStatusCount(row, key, rollupFields) {
  const rollupFieldList = Array.isArray(rollupFields)
    ? rollupFields
    : [rollupFields];
  const firstExistingField = rollupFieldList.find(
    field => row?.[field] != null && Number.isFinite(Number(row?.[field])),
  );
  if (firstExistingField) {
    const count = Number(row[firstExistingField]);
    return count;
  }
  const list = Array.isArray(row?.daily_hours) ? row.daily_hours : [];
  const count = list.reduce((acc, d) => acc + (d?.[key] ? 1 : 0), 0);
  return count;
}

/**
 * Read-only table of worker timesheet list rows from the dashboard API.
 *
 * @param {Object} props
 * @param {Object[]} props.timesheetList
 * @param {string} props.sortBy - `created_at` | `month` | `id`
 * @param {string} props.sortDirection - `asc` | `desc`
 * @param {function(string,string): void} props.onSort
 * @param {function(): void} [props.onDelete] - Refetch list after successful delete.
 * @returns {JSX.Element}
 */
const WorkerTimesheetsTable = ({
  timesheetList,
  sortBy = 'created_at',
  sortDirection = 'desc',
  onSort,
  onDelete,
}) => {
  /**
   * Shortens notes for table preview.
   *
   * @param {string|null|undefined} text
   * @param {number} max
   * @returns {string}
   */
  const truncate = (text, max = 48) => {
    const s = text == null ? '' : String(text).trim();
    if (!s) return '';
    return s.length <= max ? s : `${s.slice(0, max)}…`;
  };

  /**
   * Normalizes API month (ISO first day) to YYYY-MM label.
   *
   * @param {string|null|undefined} isoMonth
   * @returns {string}
   */
  const monthLabel = isoMonth => {
    if (!isoMonth || typeof isoMonth !== 'string') return '';
    return isoMonth.length >= 7 ? isoMonth.slice(0, 7) : isoMonth;
  };

  return (
    <Table striped hover responsive className="align-middle" dir="rtl" lang="he">
      <thead>
        <tr>
          <SortableHeaderCell
            label="מזהה"
            sortKey="id"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <th>עובד</th>
          <th>תעודת זהות</th>
          <SortableHeaderCell
            label="חודש"
            sortKey="month"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <th
            className="text-end"
            title="סיכום שרת: מספר הימים שסומנו כיום מחלה."
          >
            ימי מחלה
          </th>
          <th
            className="text-end"
            title="סיכום שרת: מספר הימים שסומנו כיום חופשה/חופש."
          >
            ימי חופשה
          </th>
          <th>הערות</th>
          <th
            className="text-end"
            title="סיכום שרת: מספר הימים שבהם סכום שעות בסיס+צהרון+ישיבה+השתלמות+שעות משתנות+שעות הובלה גדול מאפס."
          >
            ימי עבודה
          </th>
          <th
            className="text-end"
            title="סיכום שרת: סכום כל השעות בכל הימים (שעות בסיס+צהרון+ישיבה+השתלמות+שעות משתנות+שעות הובלה)."
          >
            סה״כ שעות
          </th>
          <SortableHeaderCell
            label="נוצר בתאריך"
            sortKey="created_at"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <th>פעולות</th>
        </tr>
      </thead>
      <tbody>
        {timesheetList.map(row => (
          <tr key={row.id}>
            <td>{row.id}</td>
            <td>
              {row.first_name} {row.last_name}
            </td>
            <td dir="auto" className="text-end">
              {row.worker_reference_id || '—'}
            </td>
            <td>{monthLabel(row.month)}</td>
            <td className="text-end">
              {getDayStatusCount(row, 'is_sick_day', [
                'total_sick_days',
                'sick_days_count',
                'sick_days',
                'total_sick_day_count',
              ])}
            </td>
            <td className="text-end">
              {getDayStatusCount(row, 'is_off_day', [
                'total_off_days',
                'off_days_count',
                'vacation_days_count',
                'off_days',
                'total_off_day_count',
              ])}
            </td>
            <td dir="auto" className="small text-muted">
              {truncate(row.notes) || '—'}
            </td>
            <td className="text-end">{row.total_days_worked ?? 0}</td>
            <td className="text-end">{formatTotalHours(row.total_hours_worked)}</td>
            <td className="small">
              {row.created_at ? formatApiDate(row.created_at) : '—'}
            </td>
            <td className="d-flex flex-row justify-content-center align-items-center gap-2">
              <Link
                to={`/worker-timesheets/${row.id}`}
                className="btn btn-outline-primary btn-sm"
                title="צפייה בפרטים"
              >
                <Eye className="ms-1" size={16} />
                צפייה
              </Link>
              <DeleteButton
                objectType="timesheet"
                objectName={`#${row.id} — ${row.first_name} ${row.last_name} (${monthLabel(row.month)})`}
                objectId={row.id}
                deleteFetchFunc={deleteWorkerTimesheet}
                onSuccessfulDelete={onDelete}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default WorkerTimesheetsTable;
